import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Tabs,
  Table,
  Badge,
  Group,
  Text,
  Card,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { AutoRefreshControls } from './AutoRefreshControls';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

interface K8sResource {
  name: string;
  namespace?: string;
  kind: string;
  status: string;
  age: string;
  metadata?: Record<string, any>;
}

interface ResourcesViewerProps {
  environmentId: string;
}

export function ResourcesViewer({ environmentId }: ResourcesViewerProps) {
  const [activeTab, setActiveTab] = useState<string | null>('all');
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ 
    defaultEnabled: true, 
    interval: 30000 
  });

  // Fetch resources from API
  const {
    data: resources,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['environment-resources', environmentId],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/environments/${environmentId}/resources`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }
      const data = await response.json();
      return data.data as {
        pods: K8sResource[];
        pvcs: K8sResource[];
        ingresses: K8sResource[];
        services: K8sResource[];
      };
    },
    refetchInterval, // Auto-refresh every 30s
  });

  const getStatusColor = (status: string) => {
    if (!status) return 'gray';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('running') || statusLower.includes('bound') || statusLower.includes('ready')) {
      return 'green';
    }
    if (statusLower.includes('pending') || statusLower.includes('creating')) {
      return 'yellow';
    }
    if (statusLower.includes('failed') || statusLower.includes('error')) {
      return 'red';
    }
    return 'gray';
  };

  const renderResourceTable = (resourceList: K8sResource[], kind: string) => {
    if (resourceList.length === 0) {
      return (
        <Card withBorder>
          <Center h={200}>
            <Stack align="center">
              <Text c="dimmed" size="sm">
                No {kind} found
              </Text>
            </Stack>
          </Center>
        </Card>
      );
    }

    return (
      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Age</Table.Th>
              {kind === 'Pod' && <Table.Th>Restarts</Table.Th>}
              {kind === 'Ingress' && <Table.Th>Hosts</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {resourceList.map((resource, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {resource.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusColor(resource.status)} size="sm">
                    {resource.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {resource.age}
                  </Text>
                </Table.Td>
                {kind === 'Pod' && (
                  <Table.Td>
                    <Text size="sm">{resource.metadata?.restarts || 0}</Text>
                  </Table.Td>
                )}
                {kind === 'Ingress' && (
                  <Table.Td>
                    <Text size="sm">{resource.metadata?.hosts?.join(', ') || '—'}</Text>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    );
  };

  if (error) {
    return (
      <Alert icon={<IconInfoCircle />} title="Error" color="red">
        {error instanceof Error ? error.message : 'Failed to fetch resources'}
      </Alert>
    );
  }

  if (isLoading || !resources) {
    return (
      <Card withBorder>
        <Center h={400}>
          <Loader />
        </Center>
      </Card>
    );
  }

  const allResources = [
    ...(resources.pods || []),
    ...(resources.pvcs || []),
    ...(resources.ingresses || []),
    ...(resources.services || []),
  ];

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {dataUpdatedAt
            ? `Last updated ${formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })}`
            : 'Never updated'}
        </Text>
        <AutoRefreshControls
          enabled={autoRefresh}
          onEnabledChange={setAutoRefresh}
          intervalLabel="(30s)"
          onManualRefresh={refetch}
          isRefreshing={isLoading}
        />
      </Group>

      {/* Tabs for resource types */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="all">
            All
            <Badge ml="xs" size="sm" variant="light">
              {allResources.length}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="pods">
            Pods
            <Badge ml="xs" size="sm" variant="light">
              {resources.pods?.length || 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="pvcs">
            PVCs
            <Badge ml="xs" size="sm" variant="light">
              {resources.pvcs?.length || 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="ingresses">
            Ingresses
            <Badge ml="xs" size="sm" variant="light">
              {resources.ingresses?.length || 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="services">
            Services
            <Badge ml="xs" size="sm" variant="light">
              {resources.services?.length || 0}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all" pt="md">
          <Stack gap="md">
            {resources.pods && resources.pods.length > 0 && (
              <div>
                <Text fw={500} mb="xs">
                  Pods
                </Text>
                {renderResourceTable(resources.pods, 'Pod')}
              </div>
            )}
            {resources.services && resources.services.length > 0 && (
              <div>
                <Text fw={500} mb="xs">
                  Services
                </Text>
                {renderResourceTable(resources.services, 'Service')}
              </div>
            )}
            {resources.ingresses && resources.ingresses.length > 0 && (
              <div>
                <Text fw={500} mb="xs">
                  Ingresses
                </Text>
                {renderResourceTable(resources.ingresses, 'Ingress')}
              </div>
            )}
            {resources.pvcs && resources.pvcs.length > 0 && (
              <div>
                <Text fw={500} mb="xs">
                  Persistent Volume Claims
                </Text>
                {renderResourceTable(resources.pvcs, 'PVC')}
              </div>
            )}
            {allResources.length === 0 && (
              <Card withBorder>
                <Center h={200}>
                  <Text c="dimmed" size="sm">
                    No resources found
                  </Text>
                </Center>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="pods" pt="md">
          {renderResourceTable(resources.pods || [], 'Pod')}
        </Tabs.Panel>

        <Tabs.Panel value="pvcs" pt="md">
          {renderResourceTable(resources.pvcs || [], 'PVC')}
        </Tabs.Panel>

        <Tabs.Panel value="ingresses" pt="md">
          {renderResourceTable(resources.ingresses || [], 'Ingress')}
        </Tabs.Panel>

        <Tabs.Panel value="services" pt="md">
          {renderResourceTable(resources.services || [], 'Service')}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

